import { RadioChecked, RadioLess, RadioUnchecked } from '@assets/icons';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import { Colors } from '@constants/colors.constants';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Checkbox, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, styled } from '@mui/material';
import {
  GridCellMode,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridTreeNodeWithRender,
  GridValueGetterParams
} from '@mui/x-data-grid-pro';
import { AuditOfferGroupedDto, AuditOfferResponseExpendedDto } from '@store/audits/audit-offer/audit-offer.model';
import { auditsOfferService } from '@store/audits/audit-offer/audit-offer.services';
import React, { useEffect, useState } from 'react';

const HeaderStyled = styled(TableCell)({
  backgroundColor: Colors.white
});

interface SubDataTable {
  id: number;
  label?: string;
  data: string | Date | number;
}

interface DataTable {
  id: number;
  containerName: string;
  auditOffers: AuditOfferResponseExpendedDto[];
  subHeader: SubDataTable[];
}
interface AuditsTableProps<T extends AuditOfferGroupedDto> {
  data: T[];
  columns: GridColDef[];
  handleSelectedRows?: (rows: number[]) => void;
  handleSelectedDetails?: (id: number) => void;
  groupedBy: keyof T;
  loading?: boolean;
  enableContainerDetails?: boolean;
  selectedRows?: number[];
}

const AuditsTable = <T extends AuditOfferGroupedDto>(props: AuditsTableProps<T>) => {
  const { data, columns, handleSelectedRows, selectedRows, loading, enableContainerDetails, handleSelectedDetails } = props;

  const sortedData = data.flatMap((it) => it.auditOffers).sort((a, b) => a.id - b.id);
  const allselected = selectedRows?.length === sortedData.length;
  const headers = columns.map((column) => ({ label: column.headerName, width: column.width }));

  const [tmpStrMap, setTmpStrMap] = useState({});

  const setTmpStr = (id, value) => {
    setTmpStrMap((prev) => ({
      ...prev,
      [id]: value
    }));
  };


  const areAllItemsSelected = (items: AuditOfferResponseExpendedDto[]) =>
    items.every((it) => selectedRows?.includes(it.id));

  const getSubHeaderData = (key: string, value: AuditOfferGroupedDto) => {
    switch (key) {
      case 'Contenant':
        return value.containerName;
      case 'Libellé de la prestation':
        return value.label;
      case 'Date de début':
        return value.fromDate;
      case 'Date de fin':
        return value.toDate;
      case 'Contrat':
        return value.contractId;
      case 'Prestation':
        return value.wasteName;
      default:
        return '';
    }
  };

  const newData: DataTable[] = data.flatMap((it) => {
    const itContainer: SubDataTable[] = columns.map((column) => ({
      id: it.auditOperationId,
      label: column.headerName,
      data: getSubHeaderData(column.headerName as string, it) ?? it[column.field],
    }));
    itContainer.shift();
    return [{ id: it.auditOperationId, containerName: it.containerName, auditOffers: it.auditOffers, subHeader: itContainer }];
  });

  const selectAllRows = () => {
    const rows = !allselected ? sortedData.map((it) => it.id) : [];
    handleSelectedRows?.(rows);
  };

  const selectGroupedRows = (groupedRows: AuditOfferResponseExpendedDto[]) => {
    const groupedRowIds = groupedRows.map((it) => it.id);
    const selectedGroupedRowIds = groupedRowIds.filter((id) => selectedRows?.includes(id));

    let rows;
    if (selectedGroupedRowIds.length === groupedRowIds.length) {
      rows = selectedRows?.filter((id) => !groupedRowIds.includes(id));
    } else {
      const missingRowIds = groupedRowIds.filter((id) => !selectedRows?.includes(id));
      rows = [...(selectedRows ?? []), ...missingRowIds];
    }
    handleSelectedRows?.(rows);
  };

  const selectRows = (row: number) => {
    const rows = selectedRows?.includes(row)
      ? selectedRows.filter((it) => it !== row)
      : [...(selectedRows ?? []), row];
    handleSelectedRows?.(rows);
  };

  useEffect(() => {
    const headerCells = document.querySelectorAll('.MuiTableHead-root th');
    const bodyCells = document.querySelectorAll('.MuiTableBody-root tr:first-child td');

    headerCells.forEach((th, i) => {
      if (bodyCells[i]) {
        (bodyCells[i] as any).style.width = `${(th as any).offsetWidth}px`;
      }
    });
  }, []);

  if (loading) return (
    <Stack width={'100%'} height={'55vh'} justifyContent='center' alignItems='center'>
      <CircularProgress size={30} style={{ alignItems: 'center', justifyContent: 'center' }} />
    </Stack>
  );

  const renderRow = (item: AuditOfferResponseExpendedDto, idx: number) => {
    return (
      <TableRow key={idx} sx={{ ':hover': { backgroundColor: '#F5F5F5' }, borderTop: '1px solid #848F9D' }}>
        {columns.map((column, index) => {
          const className = column.cellClassName?.toString().includes('bold') ? { fontWeight: 'bold' } : undefined;

          let rawValue = item[column.field];

          const test: GridValueGetterParams<any, any, GridTreeNodeWithRender> = {
            id: rawValue?.id as GridRowId,
            value: rawValue,
            api: '' as any,
            field: '',
            row: item,
            rowNode: '' as any,
            colDef: column as any,
            tabIndex: 0,
            cellMode: 'view' as GridCellMode,
            hasFocus: false
          };


          if (column.valueGetter) {
            rawValue = column.valueGetter(test);
          }

          let formattedValue = rawValue;
          if (column.valueFormatter) {
            formattedValue = column.valueFormatter(rawValue);
          }



          const mockParams: GridRenderCellParams<any, any, any, GridTreeNodeWithRender> = {
            id: rawValue?.id as GridRowId,
            value: rawValue,
            api: '' as any,
            cellMode: 'view' as GridCellMode,
            hasFocus: false,
            isEditable: false,
            tabIndex: -1,
            row: item,
            rowNode: '' as any,
            field: '',
            formattedValue: formattedValue,
            colDef: column as any,
          };


          return <TableCell key={index} sx={{ ...className }} align="left" width={column.width}>
            {index === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checkedIcon={<RadioChecked />}
                  indeterminateIcon={<RadioLess />}
                  icon={<RadioUnchecked />}
                  checked={selectedRows?.some((it) => it === item.id)}
                  onChange={() => selectRows(item.id)}
                  size="small"
                  sx={{ padding: '0px', marginRight: '8px' }}
                />
                <Typography noWrap>
                  {formattedValue}
                </Typography>
              </Box>
            ) : column.renderCell ? (
              column.renderCell(mockParams)
            ) : column.renderEditCell ? (
              column.renderEditCell(mockParams)
            ) : (
              formattedValue
            )}
          </TableCell>;
        })}
      </TableRow>
    );
  };


  const DisplaySubHeader = (item: SubDataTable) => {
    const resetFields = { enableSchedule: undefined, canBeRequested: undefined };
    switch (item.label) {
      case 'Libellé de la prestation':
        return (
          <TextFieldComponent
            handleChange={(value) => {
              setTmpStr(item.id, value);
              return auditsOfferService.setAction({ label: value, auditOfferIds: [item.id] });
            }}
            placeholder="-"
            value={tmpStrMap[item.id] || item.data as string} />
        );
      case 'Date de début':
        return (
          <DatePickerComponent
            inputBaseStyle={{
              backgroundColor: '#F4F6F8 !important',
              borderRadius: '6px',
            }}
            inputStyle={{ paddingLeft: '6px', backgroundColor: '#F4F6F8 !important' }}
            placeholder="-"
            value={item.data as Date}
            handleChange={(value) => {
              const formattedValue = new Date(value?.setHours(10) ?? new Date().setHours(10));
              auditsOfferService.setAction({ ...resetFields, validFromDate: formattedValue, auditOfferIds: [item.id] });
            }
          }
          />
        );
      case 'Date de fin':
        return (
          <DatePickerComponent
            inputBaseStyle={{
              backgroundColor: '#F4F6F8 !important',
              borderRadius: '6px',
            }}
            inputStyle={{ paddingLeft: '6px', backgroundColor: '#F4F6F8 !important' }}
            placeholder="-"
            value={item.data as Date}
            handleChange={(value) => {
              const formattedValue = new Date(value?.setHours(10) ?? new Date().setHours(10));
              auditsOfferService.setAction({ ...resetFields, validToDate: formattedValue, auditOfferIds: [item.id] });
            }
          }
          />
        );
      case 'Contrat':
        return (
          <IconButton disabled={item.data === null} onClick={() => auditsOfferService.setContract({
            id: item.data as number,
            open: true
          })}>
            <ArrowCircleRightOutlinedIcon color="primary" style={{ opacity: item.data === null ? '0.3' : '1', color: item.data === null ? 'grey' : undefined }} />
          </IconButton>
        );
      case 'Prestation':
        return (
          <Typography>
            {item.data as string}
          </Typography>
        );
      default:
        return '';
    }
  };

  const renderContainerDetails = (item: SubDataTable, idx: number) => {
    return (
      <HeaderStyled key={idx} sx={{ paddingTop: '30px', borderBottom: '1px solid #848F9D' }}>
        {DisplaySubHeader(item)}
      </HeaderStyled>
    );
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', height: 'calc(100vh - 28rem)', paddingY: '20px', overflowY: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ height: '55px' }}>
            {headers.map((hearderRow, idx) => (
              <HeaderStyled key={idx} width={hearderRow.width} sx={{ whiteSpace: 'nowrap', borderBottom: '1px solid #848F9D' }}>
                {idx === 0 ?
                  <Stack direction='row' width='fit-content' alignItems='center'>
                    <Checkbox
                      checked={allselected}
                      onChange={() => selectAllRows()}
                      checkedIcon={<RadioChecked />}
                      indeterminateIcon={<RadioLess />}
                      icon={<RadioUnchecked />}
                      size="small"
                      sx={{ padding: 0, marginRight: '8px' }}
                    />
                    <Typography fontSize='14px' fontWeight={700}>{hearderRow.label}</Typography>
                  </Stack>
                  : <Typography fontSize='14px' fontWeight={700}>{hearderRow.label}</Typography>
                }
              </HeaderStyled>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {
            newData.map((row, indexArray) => {
              return (
                <React.Fragment key={`$${row} + ${indexArray}`}>
                  <TableRow>
                    <HeaderStyled sx={{ paddingTop: '30px', borderBottom: '1px solid #848F9D' }}>
                      <Box display={'flex'} flexDirection='row' height={'40px'} alignItems={'center'}>
                        <Checkbox
                          checkedIcon={<RadioChecked />}
                          indeterminateIcon={<RadioLess />}
                          icon={<RadioUnchecked />}
                          checked={areAllItemsSelected(row.auditOffers)}
                          onChange={() => selectGroupedRows(row.auditOffers)}
                          size="small"
                          sx={{ padding: 0, marginRight: '8px' }}
                        />
                        <Typography fontSize="14px" fontWeight={700} color="textPrimary" noWrap>
                          {row.containerName}
                        </Typography>
                        <IconButton onClick={() => handleSelectedDetails?.(row.id)}>
                          <InfoIcon htmlColor="#192133" sx={{ height: '15px', width: '15px' }} />
                        </IconButton>
                      </Box>
                    </HeaderStyled>
                    {enableContainerDetails ? row.subHeader.map(renderContainerDetails) : null}
                  </TableRow>
                  {row.auditOffers.map(renderRow)}
                </React.Fragment>
              );
            })}
        </TableBody>
      </Table >
    </TableContainer >
  );
};

export default AuditsTable;